import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ColorBlindMode = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia' | 'achromatopsia';

const COLOR_BLIND_FILTERS: Record<ColorBlindMode, string> = {
  none: 'none',
  protanopia: 'url(#protanopia-filter)',
  deuteranopia: 'url(#deuteranopia-filter)',
  tritanopia: 'url(#tritanopia-filter)',
  achromatopsia: 'url(#achromatopsia-filter)'
};

/** Recursos de acessibilidade para daltonismo, via filtros SVG aplicados no documento. */
@Injectable({
  providedIn: 'root'
})
export class AccessibilityService {
  private readonly STORAGE_KEY = 'accessibility-colorblind-mode';
  private colorBlindModeSubject = new BehaviorSubject<ColorBlindMode>(this.getStoredMode());
  public colorBlindMode$: Observable<ColorBlindMode> = this.colorBlindModeSubject.asObservable();

  constructor() {
    this.initializeAccessibility();
  }

  private initializeAccessibility(): void {
    const mode = this.getStoredMode();
    this.applyColorBlindMode(mode);
    this.injectSVGFilters();
  }

  private getStoredMode(): ColorBlindMode {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return (stored as ColorBlindMode) || 'none';
  }

  private storeMode(mode: ColorBlindMode): void {
    localStorage.setItem(this.STORAGE_KEY, mode);
  }

  private injectSVGFilters(): void {
    if (document.getElementById('accessibility-svg-filters')) {
      return;
    }

    if (!document.body) {
      setTimeout(() => this.injectSVGFilters(), 10);
      return;
    }

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('id', 'accessibility-svg-filters');
    svg.style.position = 'absolute';
    svg.style.width = '0';
    svg.style.height = '0';
    svg.innerHTML = `
      <defs>
        <!-- Filtro para Protanopia (dificuldade em perceber vermelho) -->
        <filter id="protanopia-filter">
          <feColorMatrix type="matrix" values="
            0.567, 0.433, 0.000, 0, 0
            0.558, 0.442, 0.000, 0, 0
            0.000, 0.242, 0.758, 0, 0
            0.000, 0.000, 0.000, 1, 0
          "/>
        </filter>

        <!-- Filtro para Deuteranopia (dificuldade em perceber verde) -->
        <filter id="deuteranopia-filter">
          <feColorMatrix type="matrix" values="
            0.625, 0.375, 0.000, 0, 0
            0.700, 0.300, 0.000, 0, 0
            0.000, 0.300, 0.700, 0, 0
            0.000, 0.000, 0.000, 1, 0
          "/>
        </filter>

        <!-- Filtro para Tritanopia (dificuldade em perceber azul) -->
        <filter id="tritanopia-filter">
          <feColorMatrix type="matrix" values="
            0.950, 0.050, 0.000, 0, 0
            0.000, 0.433, 0.567, 0, 0
            0.000, 0.475, 0.525, 0, 0
            0.000, 0.000, 0.000, 1, 0
          "/>
        </filter>

        <!-- Filtro para Achromatopsia (visão em escala de cinza) -->
        <filter id="achromatopsia-filter">
          <feColorMatrix type="matrix" values="
            0.299, 0.587, 0.114, 0, 0
            0.299, 0.587, 0.114, 0, 0
            0.299, 0.587, 0.114, 0, 0
            0.000, 0.000, 0.000, 1, 0
          "/>
        </filter>
      </defs>
    `;

    document.body.appendChild(svg);
  }

  private applyColorBlindMode(mode: ColorBlindMode): void {
    const htmlElement = document.documentElement;

    if (mode === 'none') {
      // Múltiplas estratégias de remoção — uma só não é suficiente em todos os browsers
      htmlElement.style.removeProperty('filter');
      htmlElement.style.removeProperty('webkitFilter');
      htmlElement.style.filter = '';
      htmlElement.removeAttribute('data-color-blind-mode');
      htmlElement.removeAttribute('style');
      void htmlElement.offsetHeight;

      const computedStyle = window.getComputedStyle(htmlElement);
      const filterValue = computedStyle.filter;
    } else {
      htmlElement.setAttribute('data-color-blind-mode', mode);
      htmlElement.style.filter = COLOR_BLIND_FILTERS[mode];
    }

    this.colorBlindModeSubject.next(mode);
  }

  /** Usado ao restaurar configurações padrão. */
  forceRemoveFilters(): void {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;

    htmlElement.style.removeProperty('filter');
    htmlElement.style.removeProperty('webkitFilter');
    htmlElement.style.filter = '';
    htmlElement.style.filter = 'none';
    htmlElement.removeAttribute('data-color-blind-mode');

    if (bodyElement) {
      bodyElement.style.removeProperty('filter');
      bodyElement.style.removeProperty('webkitFilter');
      bodyElement.style.filter = '';
      bodyElement.style.filter = 'none';
    }

    htmlElement.removeAttribute('style');

    void htmlElement.offsetHeight;
    void htmlElement.offsetWidth;
    void htmlElement.clientHeight;

    const computedStyle = window.getComputedStyle(htmlElement);
    void computedStyle.filter;

    this.storeMode('none');
    this.colorBlindModeSubject.next('none');
  }

  setColorBlindMode(mode: ColorBlindMode): void {
    this.storeMode(mode);
    this.applyColorBlindMode(mode);
  }

  getCurrentMode(): ColorBlindMode {
    return this.colorBlindModeSubject.value;
  }

  getAvailableModes(): Array<{ value: ColorBlindMode; label: string; description: string }> {
    return [
      {
        value: 'none',
        label: 'Nenhum',
        description: 'Cores padrão sem ajustes'
      },
      {
        value: 'protanopia',
        label: 'Protanopia',
        description: 'Dificuldade em perceber vermelho'
      },
      {
        value: 'deuteranopia',
        label: 'Deuteranopia',
        description: 'Dificuldade em perceber verde'
      },
      {
        value: 'tritanopia',
        label: 'Tritanopia',
        description: 'Dificuldade em perceber azul'
      },
      {
        value: 'achromatopsia',
        label: 'Achromatopsia',
        description: 'Visão em escala de cinza'
      }
    ];
  }
}
